// ─── Form Field Templates ─────────────────────────────────────────────────────

let _id = 1;
const uid = () => `tpl_${_id++}_${Math.random().toString(36).slice(2, 6)}`;

const f = (type, label, extra = {}) => ({ id: uid(), type, _type: type, label, required: false, helperText: '', ...extra });
const opt = (label, value) => ({ label, value });
const req = (field) => ({ ...field, required: true });

// ─── Registration template system fields (login-method-aware) ─────────────────
// Returns the correct locked system fields for the top of an event-registration
// form based on the event's configured login method.
//
//  'email'          → system email + system password + firstName + lastName + phone
//  'employeeNumber' → system employeeNumber (labelled dynamically) + email (non-system)
//                     + firstName + lastName  (no password)
//  'magicLink'      → system email (mapped to magicLink key) + firstName + lastName
//                     (no password)
//
// All fields that are part of the login credential set are _systemField: true so the
// builder locks them and the FormBuilderTab injection logic stays in sync.

const SYS_FIRST_NAME = { id: '__sys_firstName__', type: 'firstName', _type: 'firstName', label: 'First Name',   placeholder: 'Enter your first name', required: false, helperText: '', _systemField: true, _systemKey: 'firstName' };
const SYS_LAST_NAME  = { id: '__sys_lastName__',  type: 'lastName',  _type: 'lastName',  label: 'Last Name',    placeholder: 'Enter your last name',  required: false, helperText: '', _systemField: true, _systemKey: 'lastName'  };
const SYS_PHONE      = { id: '__sys_phone__',     type: 'phone',     _type: 'phone',     label: 'Phone Number', placeholder: '+1 (555) 000-0000',     required: false, helperText: '', _systemField: true, _systemKey: 'phone'     };

function buildRegistrationSystemFields(loginMethod = 'email', employeeFieldLabel = 'Employee Number') {
  switch (loginMethod) {
    case 'email':
      // Email + password login — both credential fields are system-locked
      return [
        { id: '__sys_email__',    type: 'email',    _type: 'email',    label: 'Email Address', placeholder: 'you@example.com',      required: true,  helperText: '', _systemField: true, _systemKey: 'email'    },
        { id: '__sys_password__', type: 'password', _type: 'password', label: 'Password',      placeholder: 'Create a password',    required: true,  helperText: '', _systemField: true, _systemKey: 'password' },
        SYS_FIRST_NAME,
        SYS_LAST_NAME,
        SYS_PHONE,
      ];

    case 'employeeNumber':
      // Employee-ID login — ID is system-locked; email is collected but not a credential
      return [
        { id: '__sys_empnum__', type: 'text', _type: 'text', label: employeeFieldLabel, placeholder: 'Enter your employee number', required: true, helperText: '', _systemField: true, _systemKey: 'employeeNumber' },
        { id: '__sys_email__',  type: 'email', _type: 'email', label: 'Email Address', placeholder: 'you@example.com',            required: true, helperText: '', _systemField: true, _systemKey: 'email'          },
        SYS_FIRST_NAME,
        SYS_LAST_NAME,
      ];

    case 'magicLink':
      // Magic-link login — email is the credential; no password field
      return [
        { id: '__sys_email__', type: 'email', _type: 'email', label: 'Email Address', placeholder: 'you@example.com', required: true, helperText: '', _systemField: true, _systemKey: 'magicLink' },
        SYS_FIRST_NAME,
        SYS_LAST_NAME,
      ];

    default:
      return [SYS_FIRST_NAME, SYS_LAST_NAME, SYS_PHONE];
  }
}

// Non-credential fields shared by every registration variant
const REGISTRATION_EXTRA_FIELDS = [
  req(f('select', 'Job Title', {
    options: [
      opt('Student',      'student'),
      opt('Professional', 'professional'),
      opt('Manager',      'manager'),
      opt('Executive',    'executive'),
      opt('Other',        'other'),
    ],
  })),
  f('text',  'Company / Organization', { placeholder: 'e.g. Acme Corp' }),
  f('radio', 'How did you hear about us?', {
    options: [
      opt('Social Media',       'social'),
      opt('Email',              'email'),
      opt('Friend / Colleague', 'referral'),
      opt('Search Engine',      'search'),
      opt('Other',              'other'),
    ],
  }),
  f('toggle', 'I agree to the event terms and conditions', { required: true }),
];

export const FORM_TEMPLATES = [
  {
    id: 'event-registration',
    name: 'Event Registration',
    description: 'Standard attendee sign-up with name, contact info, and preferences.',
    icon: '',
    accent: '#1075BA',
    bg: 'linear-gradient(135deg, #eef4ff 0%, #dce8fa 100%)',
    layout:   'card-left',
    isRegistration: true,
    headerTitle:    'RESERVE YOUR SLOT',
    headerSubtitle: 'Event Registration',
    description2:   'Fill in your details below to secure your spot.',
    heroTextColor:  '#ffffff',

    // getFields is called by handleApplyTemplate / FormWizard instead of reading
    // .fields directly. It returns system fields tailored to the login method,
    // followed by the shared non-credential fields.
    getFields(loginMethod = 'email', employeeFieldLabel = 'Employee Number') {
      return [
        ...buildRegistrationSystemFields(loginMethod, employeeFieldLabel),
        ...REGISTRATION_EXTRA_FIELDS,
      ];
    },

    // Fallback static fields used only when no event context is available
    // (e.g. standalone form wizard before an event is loaded). Defaults to
    // the email+password layout since that is the most common case.
    get fields() {
      return this.getFields('email');
    },
  },

  {
    id: 'feedback-form',
    name: 'Post-Event Feedback',
    description: 'Collect ratings, comments, and NPS score after an event.',
    icon: '',
    accent: '#f59e0b',
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    layout:   'card-left',
    headerTitle:    'SHARE YOUR THOUGHTS',
    headerSubtitle: 'How did we do?',
    description2:   'Your feedback helps us make the next event even better.',
    heroTextColor:  '#ffffff',
    fields: [
      req(f('rating',   'Overall Experience', { max: 5, helperText: '1 = Poor, 5 = Excellent' })),
      req(f('scale',    'How likely are you to recommend this event?', { min: 0, max: 10, step: 1, helperText: '0 = Not at all, 10 = Extremely likely' })),
      f('radio',        'What did you enjoy most?', {
        options: [
          opt('Speakers',     'speakers'),
          opt('Networking',   'networking'),
          opt('Content',      'content'),
          opt('Venue',        'venue'),
          opt('Organisation', 'organisation'),
        ],
      }),
      f('checkbox',     'What could be improved?', {
        options: [
          opt('More networking time',  'networking'),
          opt('Better app / platform', 'platform'),
          opt('Clearer schedule',      'schedule'),
          opt('More diverse speakers', 'speakers'),
          opt('Longer sessions',       'sessions'),
        ],
      }),
      f('textarea',     'Additional comments', { placeholder: 'Tell us more about your experience…', rows: 4 }),
      f('toggle',       'I am happy to be contacted for follow-up'),
    ],
  },

  {
    id: 'general-survey',
    name: 'General Survey',
    description: 'Gather structured opinions with ratings, scales, and open-ended questions.',
    icon: '',
    accent: '#8b5cf6',
    bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    layout:   'card-left',
    headerTitle:    'WE WANT TO HEAR FROM YOU',
    headerSubtitle: 'Quick Survey',
    description2:   'This will only take a few minutes.',
    heroTextColor:  '#ffffff',
    fields: [
      { id: '__sys_firstName__', type: 'firstName', _type: 'firstName', label: 'First Name',   placeholder: 'Enter your first name', required: false, helperText: '', _systemField: true, _systemKey: 'firstName' },
      { id: '__sys_lastName__',  type: 'lastName',  _type: 'lastName',  label: 'Last Name',    placeholder: 'Enter your last name',  required: false, helperText: '', _systemField: true, _systemKey: 'lastName'  },
      req(f('email',    'Email Address', { placeholder: 'you@example.com' })),
      req(f('radio',    'Which best describes you?', {
        options: [
          opt('Attendee',  'attendee'),
          opt('Speaker',   'speaker'),
          opt('Sponsor',   'sponsor'),
          opt('Organiser', 'organiser'),
        ],
      })),
      req(f('rating',   'How satisfied are you overall?', { max: 5 })),
      f('scale',        'Rate the event value for money', { min: 1, max: 10, step: 1 }),
      f('checkbox',     'Which sessions did you attend?', {
        options: [
          opt('Opening Keynote',  'keynote'),
          opt('Workshop A',       'workshop_a'),
          opt('Workshop B',       'workshop_b'),
          opt('Panel Discussion', 'panel'),
          opt('Closing Ceremony', 'closing'),
        ],
      }),
      f('textarea',     'What topics would you like covered next time?', { rows: 3 }),
    ],
  },

  {
    id: 'contact-inquiry',
    name: 'Contact / Inquiry',
    description: 'Simple message form for general inquiries or support requests.',
    icon: '',
    accent: '#ef4444',
    bg: 'linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%)',
    layout:   'card-left',
    headerTitle:    'GET IN TOUCH',
    headerSubtitle: 'Contact Us',
    description2:   "We'd love to hear from you. We'll get back to you as soon as possible.",
    heroTextColor:  '#ffffff',
    fields: [
      { id: '__sys_firstName__', type: 'firstName', _type: 'firstName', label: 'First Name',   placeholder: 'Enter your first name', required: false, helperText: '', _systemField: true, _systemKey: 'firstName' },
      { id: '__sys_lastName__',  type: 'lastName',  _type: 'lastName',  label: 'Last Name',    placeholder: 'Enter your last name',  required: false, helperText: '', _systemField: true, _systemKey: 'lastName'  },
      req(f('email',    'Email Address', { placeholder: 'you@example.com' })),
      { id: '__sys_phone__',     type: 'phone',     _type: 'phone',     label: 'Phone Number', placeholder: '+1 (555) 000-0000',     required: false, helperText: '', _systemField: true, _systemKey: 'phone'     },
      req(f('select',   'Reason for contacting us', {
        options: [
          opt('General Inquiry',   'general'),
          opt('Sponsorship',       'sponsorship'),
          opt('Partnership',       'partnership'),
          opt('Media / Press',     'media'),
          opt('Technical Support', 'support'),
          opt('Other',             'other'),
        ],
      })),
      req(f('text',     'Subject',  { placeholder: 'Brief summary of your message' })),
      req(f('textarea', 'Message',  { placeholder: 'Write your message here…', rows: 5 })),
      f('toggle', 'I consent to my information being stored and used to respond to my inquiry'),
    ],
  },
];